import { Calendar, Clock, ArrowRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { BlogPost } from "@/types/blogPost";

interface FeaturedArticleProps {
  featuredPost?: BlogPost | null;
  onReadPost?: (post: BlogPost) => void;
}

export function FeaturedArticle({ featuredPost, onReadPost }: FeaturedArticleProps) {
  if (!featuredPost) return null;

  const slug = (featuredPost as any).slug ?? featuredPost.id;
  const category = (featuredPost as any).blog_categories;

  const tagStyle = category?.color_hex
    ? { backgroundColor: category.color_hex + '22', color: category.color_hex }
    : undefined;

  return (
    <section className="py-10 sm:py-14 bg-white">
      <div className="container mx-auto px-4 sm:px-6">

        <div className="flex items-center gap-2 mb-6">
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400 font-primary">Artigo em Destaque</span>
        </div>

        <Link
          to={`/blog/${slug}`}
          className="group grid lg:grid-cols-[1fr_420px] gap-0 rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 bg-white"
        >
          {/* Text side */}
          <div className="p-7 sm:p-10 flex flex-col justify-center order-2 lg:order-1">
            {category && (
              <span
                className="self-start text-xs font-bold font-primary px-3 py-1 rounded-full mb-4"
                style={tagStyle}
              >
                {category.name}
              </span>
            )}
            <h2 className="text-2xl sm:text-3xl font-bold font-primary text-gray-800 leading-tight mb-3 group-hover:text-brand-primary transition-colors line-clamp-3">
              {featuredPost.title}
            </h2>
            {featuredPost.subtitle && (
              <p className="text-brand-primary font-semibold font-primary text-sm mb-3">{featuredPost.subtitle}</p>
            )}
            <p className="text-gray-500 font-primary text-sm leading-relaxed line-clamp-3 mb-6">
              {featuredPost.excerpt ?? featuredPost.content?.substring(0, 180) ?? ''}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 font-primary mb-7">
              {featuredPost.created_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(featuredPost.created_at).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              )}
              {featuredPost.read_time_minutes && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  {featuredPost.read_time_minutes} min de leitura
                </span>
              )}
            </div>

            <span className="inline-flex items-center gap-2 self-start bg-brand-primary text-white text-sm font-bold font-primary px-5 py-2.5 rounded-xl group-hover:bg-brand-primary/90 transition-colors">
              Ler artigo completo <ArrowRight className="w-4 h-4" />
            </span>
          </div>

          {/* Image side */}
          <div className="relative h-56 sm:h-72 lg:h-auto overflow-hidden order-1 lg:order-2">
            {featuredPost.featured_image_url ? (
              <img
                src={featuredPost.featured_image_url}
                alt={featuredPost.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 flex items-center justify-center">
                <span className="text-6xl opacity-20">🥗</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent lg:bg-gradient-to-l" />
          </div>
        </Link>
      </div>
    </section>
  );
}
